import React from 'react';
import { uniqueId } from 'lodash';
import { FieldArrayRenderProps, Field, FieldProps, useFormikContext } from 'formik';
import { FormGroupLabel, Dropdown, TextField, Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';

import { InformationPopUp } from 'components';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import CurrencyTextField from 'components/CurrencyTextField/CurrencyTextField';

import { employmentTypeList, EmploymentFormFields, EmploymentData } from 'contexts/FormProvider';

import validationSchema from '../schema';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
  index: number;
}

const EmploymentEdit = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const { arrayHelpers, index } = props;

  const { values } = useFormikContext<EmploymentFormFields>();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const onAddEmploymentDetails = (idx: number) => {
    const newDetails: EmploymentData = {
      id: uniqueId(),
      employmentType: '',
      employerName: '',
      occupation: '',
      contactPerson: '',
      contactPersonNumber: '',
      salary: '',
    };

    arrayHelpers.insert(idx + 1, newDetails);
  };

  const isDisabled = !validationSchema.isValidSync(values);
  const isCurrentlyEmployed = values.employmentStatus === 'yes';
  const isFull = values.employmentData.length === 5;
  const hasOneElement = values.employmentData.length === 1;

  // Localized content constants
  const sectionHeadingBase = getTextContent('Employment.details.heading');
  const employmentTypeTitle = getTextContent('Employment.details.employmentType');
  const employmentTypeLabel = getTextContent('Employment.details.employmentType.label');
  const employerNameTitle = getTextContent('Employment.details.employerName');
  const employerNameLabel = getTextContent('Employment.details.employerName.label');
  const occupationTitle = getTextContent('Employment.details.occupation');
  const occupationLabel = getTextContent('Employment.details.occupation.label');
  const contactPersonTitle = getTextContent('Employment.details.contactPerson');
  const contactPersonLabel = getTextContent('Employment.details.contactPerson');
  const contactPersonNumberTitle = getTextContent('Employment.details.contactPersonNumber');
  const contactPersonNumberLabel = getTextContent('Employment.details.contactPersonNumber.label');
  const salaryTitle = getTextContent('Employment.details.salary');
  const salaryInfoText = getTextContent('Employment.details.salaryInfo');
  const salaryLabel = getTextContent('Employment.details.salary.label');
  const addEmployerLabel = getTextContent('Employment.details.addEmployer.label');
  const removeEmployerLabel = getTextContent('Employment.details.removeEmployer.label');

  if (!isCurrentlyEmployed) return null;

  return (
    <>
      <FormSpacer type="pagesection" />
      <div ref={ref} />
      <FormCard variant="sectionTitle" title={`${sectionHeadingBase} - (${index + 1})`}>
        <FormGroupLabel title={employmentTypeTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].employmentType`}>
          {({ field, meta }: FieldProps) => (
            <Dropdown
              {...field}
              id={field.name}
              label={employmentTypeLabel}
              data-track={employmentTypeLabel}
              data-track-value="capture"
              optionList={employmentTypeList.map((i) => ({ label: i, value: i }))}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(event: any) => field.onChange(event)}
              error={meta.touched && !!meta.error}
            />
          )}
        </Field>

        <FormSpacer type="input" />

        <FormGroupLabel title={employerNameTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].employerName`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={employerNameLabel}
              data-track={employerNameLabel}
              data-track-value="mask"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="input" />

        <FormGroupLabel title={occupationTitle} required alignCenter />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].occupation`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={occupationLabel}
              data-track={occupationLabel}
              data-track-value="capture"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="input" />

        <FormGroupLabel title={contactPersonTitle} alignCenter />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].contactPerson`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={contactPersonLabel}
              data-track={contactPersonLabel}
              data-track-value="mask"
              maxLength={200}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="input" />

        <FormGroupLabel title={contactPersonNumberTitle} alignCenter />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].contactPersonNumber`}>
          {({ field, meta }: FieldProps) => (
            <TextField
              {...field}
              id={field.name}
              label={contactPersonNumberLabel}
              data-track={contactPersonNumberLabel}
              data-track-value="mask"
              maxLength={20}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="input" />

        <FormGroupLabel
          title={salaryTitle}
          titleSuffix={<InformationPopUp message={salaryInfoText} />}
          required
          alignCenter
        />
        <FormSpacer type="input" />
        <Field name={`employmentData[${index}].salary`}>
          {({ field, meta }: FieldProps) => (
            <CurrencyTextField
              {...field}
              id={field.name}
              label={salaryLabel}
              data-track={salaryLabel}
              data-track-value="mask"
              maxLength={10}
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
            />
          )}
        </Field>

        <FormSpacer type="formgroup" />

        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button
            type="button"
            label={addEmployerLabel}
            variant="secondary-on-light"
            disabled={isFull || isDisabled}
            onClick={() => onAddEmploymentDetails(index)}
          />
          <FormSpacer type="input" direction="row" />
          <Button
            type="button"
            label={removeEmployerLabel}
            data-track={removeEmployerLabel}
            variant="secondary-on-light"
            disabled={hasOneElement}
            onClick={() => arrayHelpers.remove(index)}
          />
        </Box>
      </FormCard>
    </>
  );
});

EmploymentEdit.displayName = 'EmploymentEdit';

export default EmploymentEdit;
